import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Star, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface Profile {
  id: string;
  full_name: string;
  user_type: string;
}

interface UserRating {
  id: string;
  rating: number;
  written_feedback: string | null;
  created_at: string;
  job_id: string;
  reviewer: Profile;
  job_title: string;
}

interface UserRatingsDialogProps {
  isOpen: boolean;
  onClose: () => void;
  currentUserProfile: Profile;
}

export default function UserRatingsDialog({ 
  isOpen, 
  onClose, 
  currentUserProfile 
}: UserRatingsDialogProps) {
  const [ratings, setRatings] = useState<UserRating[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    if (isOpen && currentUserProfile) {
      fetchUserRatings();
    }
  }, [isOpen, currentUserProfile]);

  const fetchUserRatings = async () => {
    setIsLoading(true);
    try {
      console.log('Fetching user ratings via RPC for profile:', currentUserProfile.id);
      
      // Use the new RPC function to get all received ratings with reviewer details
      const { data, error } = await supabase.rpc('get_received_ratings_with_reviewers');

      if (error) {
        console.error('RPC error:', error);
        throw error;
      }

      console.log('RPC response data:', data);

      if (!data || data.length === 0) {
        setRatings([]);
        return;
      }

      // Map the data to our expected format
      const ratingsWithDetails = data.map((rating: any) => ({
        id: rating.id,
        rating: rating.rating,
        written_feedback: rating.written_feedback,
        created_at: rating.created_at,
        job_id: rating.job_id,
        reviewer: {
          id: rating.reviewer_id,
          full_name: rating.reviewer_name,
          user_type: rating.reviewer_user_type,
        },
        job_title: rating.job_title
      }));

      setRatings(ratingsWithDetails);
    } catch (error) {
      console.error("Error fetching user ratings:", error);
      toast({
        title: "Error",
        description: "Failed to load your ratings. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Your Ratings & Reviews</DialogTitle>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin" />
            <span className="ml-2">Loading your ratings...</span>
          </div>
        ) : ratings.length === 0 ? (
          <div className="text-center py-8">
            <div className="text-muted-foreground">
              <Star className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <h3 className="font-medium mb-2">No ratings yet</h3>
              <p className="text-sm">Complete some jobs to start receiving ratings!</p>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="text-sm text-muted-foreground">
              Showing {ratings.length} rating{ratings.length !== 1 ? 's' : ''}
            </div>
            
            {ratings.map((rating) => (
              <Card key={rating.id}>
                <CardContent className="p-4">
                  <div className="space-y-3">
                    {/* Header with rating and reviewer */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="flex items-center gap-1">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <Star
                              key={star}
                              className={`w-4 h-4 ${
                                star <= rating.rating
                                  ? "fill-yellow-400 text-yellow-400"
                                  : "text-muted-foreground"
                              }`}
                            />
                          ))}
                        </div>
                        <span className="font-medium">{rating.rating}/5</span>
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {new Date(rating.created_at).toLocaleDateString()}
                      </div>
                    </div>

                    {/* Reviewer info */}
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium">{rating.reviewer.full_name}</span>
                      <Badge variant="secondary" className="text-xs">
                        {rating.reviewer.user_type}
                      </Badge>
                    </div>

                    {/* Job title */}
                    <div className="text-sm text-muted-foreground">
                      Job: <span className="font-medium">{rating.job_title}</span>
                    </div>

                    {/* Written feedback */}
                    {rating.written_feedback && (
                      <div className="bg-muted/50 p-3 rounded-lg">
                        <p className="text-sm">{rating.written_feedback}</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}